import { useViewNotification } from "@lvt/view-notification-service-client";
import { Button, Collapse, ListItem, Typography } from "@material-ui/core";
import { Bolt, CompassCalibration, DirectionsWalk, EmojiPeople, LocalCarWash, LocalFireDepartment, PowerOff, RemoveRedEye, WarningAmber, WatchLater } from "@mui/icons-material";
import clsx from "clsx";
import { FC, isValidElement, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Alert } from "@/utilities/types/alert";
import AlertClip from "@/views/alerts/AlertsPage/AlertItem/AlertClip/AlertClip";

import AlertIcon from "./AlertIcon/AlertIcon";
import useStyles from "./AlertItem.styles";


export interface AlertItemProps {
    alert: Alert;
    onClickResolve: () => void;
    showClientName: boolean;
}

const iconMap: { [alertType: string]: ReactNode | { [subType: string]: ReactNode } } = {
    "intrusion": {
        "person": <DirectionsWalk />,
        "vehicle": <LocalCarWash />,
        "object": <CompassCalibration />,
    },
    "powerloss": <PowerOff />,
    "low_voltage": <Bolt />,
    "loitering": <EmojiPeople />,
    "fire": <LocalFireDepartment />,
};
const defaultIcon = <WarningAmber />;

const AlertItem: FC<AlertItemProps> = ({ alert, onClickResolve: _onClickResolve, showClientName }) => {
    const { t, i18n } = useTranslation("translation", { keyPrefix: "components.alerts.alertItem" });
    const [isHovering, setIsHovering] = useState(false);

    const [viewers, connectionState] = useViewNotification("alert", `${alert.liveUnitId}.${alert.alertId}`, { monitor: true });
    const isCollapsed = connectionState === "connected" && !!viewers.length;

    const leftThumbnail = alert.media.find(m => m.cameraRole === "Left" && m.mimeType === "image/jpeg");
    const leftVideo = alert.media.find(m => m.cameraRole === "Left" && m.mimeType === "video/mp4");
    const centerThumbnail = alert.media.find(m => m.cameraRole === "Primary" && m.mimeType === "image/jpeg");
    const centerVideo = alert.media.find(m => m.cameraRole === "Primary" && m.mimeType === "video/mp4");
    const rightThumbnail = alert.media.find(m => m.cameraRole === "Right" && m.mimeType === "image/jpeg");
    const rightVideo = alert.media.find(m => m.cameraRole === "Right" && m.mimeType === "video/mp4");

    const getIcon = (alertType: string | undefined, alertSubType: string | undefined) => {
        if (!alertType) return defaultIcon;

        if (iconMap[alertType] && isValidElement(iconMap[alertType])) return iconMap[alertType];

        try {
            if (alertSubType) return (iconMap[alertType] as { [key: string]: ReactNode })?.[alertSubType] ?? defaultIcon;
        } catch {
            // Do nothing
        }

        return defaultIcon;
    };

    const getCollapsedTimestamp = () => {
        const date = new Date(alert.alertTimestamp);

        return `${date.toLocaleTimeString(i18n.language, {
            hour: "numeric",
            minute: "numeric",
            timeZoneName: "short",
        })} - ${date.toLocaleDateString(i18n.language, {
            month: "short",
            day: "numeric",
            year: "numeric",
        })}`;
    };

    const onClickResolve = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        _onClickResolve();
    };

    const classes = useStyles();

    return (
        <Link to={{ pathname: `/live-unit/${alert.liveUnitId}/view`, search: `?contextId=${alert.alertId}` }} className={classes.link}>
            {/* Collapsed item */}
            <Collapse in={isCollapsed}>
                <ListItem
                    data-testid="alert-item-collapsed"
                    className={classes.item}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    <div className={clsx(classes.leftSection, classes.rightDivider)}>
                        <div className={classes.viewer}>
                            <Typography variant="body1" className={classes.viewerText}>
                                <RemoveRedEye className={classes.viewerIcon} />
                                {viewers[0]?.username}
                            </Typography>
                        </div>
                        <Typography variant="body1" className={clsx(classes.collapsedTimestamp, classes.headerText)}>
                            {getCollapsedTimestamp()}
                        </Typography>
                    </div>

                    <div className={classes.description}>
                        <div className={classes.descriptionHeader}>
                            <Typography
                                className={clsx(classes.headerText, classes.leftHeader)}>{alert.liveUnitId} {alert.locationName}, {alert.liveUnitName}</Typography>
                            {showClientName &&
                                <>
                                    <span className={classes.leader} />
                                    <Typography
                                        className={clsx(classes.headerText, classes.rightHeader)}>{alert.clientName}</Typography>
                                </>
                            }
                        </div>
                    </div>
                </ListItem>
            </Collapse>

            {/* Expanded item */}
            <Collapse in={!isCollapsed}>
                <ListItem
                    data-testid="alert-item-expanded"
                    className={clsx(classes.item, classes.fullHeight)}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    <div className={classes.clips}>
                        <AlertClip play={!isCollapsed && isHovering} video={leftVideo} thumbnail={leftThumbnail} />
                        <AlertClip play={!isCollapsed && isHovering} video={centerVideo} thumbnail={centerThumbnail} />
                        <AlertClip play={!isCollapsed && isHovering} video={rightVideo} thumbnail={rightThumbnail} />
                    </div>

                    <div className={classes.description}>
                        {/*Description*/}
                        <div className={classes.descriptionHeader}>
                            <Typography
                                className={clsx(classes.headerText, classes.leftHeader)}>{alert.liveUnitId} {alert.locationName}, {alert.liveUnitName}</Typography>
                            {showClientName &&
                                <>
                                    <span className={classes.leader} />
                                    <Typography
                                        className={clsx(classes.headerText, classes.rightHeader)}>{alert.clientName}</Typography>
                                </>
                            }
                        </div>

                        <div className={classes.innerDescription}>
                            {/* Alert Type */}
                            <AlertIcon
                                icon={getIcon(alert.alertType, alert.alertSubject)}
                                title={
                                    // ! is marking as not undefined because default will be used if it is undefined.
                                    t([`alertType.${alert.alertType!}`, "alertTypeDefault"])
                                }
                                subTitle={alert.alertSubject && t(`alertSubject.${alert.alertSubject}`, "")}
                            />

                            {/* Alert Time */}
                            <AlertIcon
                                icon={<WatchLater />}
                                title={(new Date(alert.alertTimestamp)).toLocaleDateString(i18n.language, {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                })}
                                subTitle={(new Date(alert.alertTimestamp)).toLocaleTimeString(i18n.language, {
                                    hour: "numeric",
                                    minute: "numeric",
                                    timeZoneName: "short",
                                })}
                            />

                            <div className={classes.itemButtons}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="large"
                                    onClick={onClickResolve}
                                    className={classes.resolveButton}
                                >
                                    {t("resolve")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </ListItem>
            </Collapse>
        </Link>
    );
};

export default AlertItem;
